// Pure revenue-classification core for the Xero integration (Phase 1). Shared by
// the server sync (recompute rollups) and — via the rollup store — the
// Business-lens revenue reads. Framework-free + SDK-free: the sync maps Xero SDK
// responses down to the plain shapes below, then runs these tested helpers.
//
// THE rule (probe-confirmed 2026-07-07): account income = Σ lines whose account
// code is NOT in the excluded set. Codes 4140 (Social Media Advertising Revenue)
// and 4150 (Google Advertising Revenue) are recouped pass-through ad spend —
// retained in full, but excluded from account income. The excluded set is
// documented, adjustable CONFIG, never a magic literal buried in logic.

// The recouped-media account codes excluded from account income. Adjust here (the
// single source of truth) if Xero adds/renames a pass-through code.
export const REVENUE_EXCLUDED_ACCOUNT_CODES: readonly string[] = ['4140', '4150'];

// Sentinel used as the `byCode` MAP KEY for a line whose Xero AccountCode is
// blank. Firestore rejects an empty-string field name ("Element at index 0
// should not be an empty string"), and the rollup's byCode is a Firestore map, so
// an uncoded line must key under this sentinel rather than ''. This is ONLY the
// map key — income/excluded classification still runs on the RAW code (blank is
// not in the excluded set → still counts as accountIncome). Chosen with a leading
// underscore so it can't collide with a real numeric Xero code.
export const UNCODED_ACCOUNT_CODE = '_uncoded';

// Native-currency DOLLARS (Xero decimal, e.g. 573938.1) → integer cents
// (money-is-integer-cents rule). Rounds to the nearest cent to absorb float
// noise; null/NaN → 0.
export function dollarsToCents(amount: number | null | undefined): number {
  if (amount == null || !Number.isFinite(amount)) return 0;
  return Math.round(amount * 100);
}

// yyyy-mm from a yyyy-mm-dd (or any leading-ISO) date string; null if unparseable.
export function invoiceMonth(date: string | null | undefined): string | null {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})/.exec(String(date).trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

export function isExcludedAccountCode(
  code: string,
  excluded: readonly string[] = REVENUE_EXCLUDED_ACCOUNT_CODES,
): boolean {
  return excluded.includes(code.trim());
}

// ---- contact → account resolution ------------------------------------------

// Roster subset the resolver needs. Optional Xero-mapping fields (Account.xero*).
export type XeroMappableAccount = {
  id: string;
  xeroContactId?: string | null;
  xeroTenantId?: string | null;
};

// Index key domain-separates tenant + contact (a contactId is only unique within
// its org). A NUL separator can't appear in a Xero id, so it's collision-safe.
export function contactAccountKey(tenantId: string, contactId: string): string {
  return `${tenantId} ${contactId}`;
}

// Build the current contactId → kyck accountId map from the roster. Only accounts
// with BOTH xeroTenantId + xeroContactId participate. Later duplicate mappings
// (two accounts claiming one contact — shouldn't happen) keep the first, stably.
export function buildContactAccountIndex(accounts: XeroMappableAccount[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const a of accounts) {
    if (!a.xeroTenantId || !a.xeroContactId) continue;
    const key = contactAccountKey(a.xeroTenantId, a.xeroContactId);
    if (!index.has(key)) index.set(key, a.id);
  }
  return index;
}

// Resolve one line to a kyck account: an explicit per-line override wins (the
// line-item attribution plumbing — §3b at line level; Phase 1 leaves overrides
// null), else the invoice contact's mapped account (null = unmapped contact).
// Phase 1b generalises this to allocations[] (src/lib/xeroAllocation.ts); this
// single-account resolver is kept for the non-group Phase-1 path + callers.
export function resolveLineAccountId(
  overrideAccountId: string | null,
  contactAccountId: string | null,
): string | null {
  return overrideAccountId ?? contactAccountId;
}

// ---- rollup aggregation ----------------------------------------------------

// One invoice line, already resolved to an account (or null = unattributed). In
// Phase 1b a grouped line is EXPANDED into several of these — one per allocation
// account, each carrying that account's cent share of the line.
export type ResolvedRevenueLine = {
  invoiceId: string;
  accountId: string | null;
  month: string; // yyyy-mm (caller guarantees non-null)
  currency: string;
  accountCode: string; // '' when Xero omitted it
  amountCents: number;
};

// A grouped-invoice line whose allocation isn't confirmed yet (Phase 1b). Held
// on the GROUP account, OUT of account income, until the operator confirms the
// split — never silently mis-attributed.
export type PendingRevenueLine = {
  accountId: string; // the group account the line's contact maps to
  month: string;
  currency: string;
  amountCents: number;
};

// A per-(account, month, currency) rollup. byCode carries EVERY code's cents
// (income + excluded) so the later "revenue by area" / per-code view has the raw
// breakdown; the two headline figures are derived from it via the excluded set.
export type RevenueRollup = {
  accountId: string;
  month: string;
  currency: string;
  accountIncomeCents: number; // Σ non-excluded codes
  mediaSpendRecoupedCents: number; // Σ excluded codes (4140/4150)
  // Grouped-invoice revenue awaiting an allocation split (Phase 1b) — held on the
  // group, excluded from accountIncomeCents until confirmed.
  pendingAllocationCents: number;
  byCode: Record<string, number>; // code → cents (blank code keyed as UNCODED_ACCOUNT_CODE)
  invoiceCount: number; // distinct invoices touching this rollup
};

export type RevenueAggregation = {
  rollups: RevenueRollup[];
  // Lines whose contact isn't mapped to an account (surfaced as a review count,
  // never dropped — the invoice line itself is still stored raw).
  unattributedLineCount: number;
  unattributedCents: number;
};

type Bucket = RevenueRollup & { _invoiceIds: Set<string> };

function bucketKey(accountId: string, month: string, currency: string): string {
  return `${accountId} ${month} ${currency}`;
}

function emptyBucket(accountId: string, month: string, currency: string): Bucket {
  return {
    accountId,
    month,
    currency,
    accountIncomeCents: 0,
    mediaSpendRecoupedCents: 0,
    pendingAllocationCents: 0,
    byCode: {},
    invoiceCount: 0,
    _invoiceIds: new Set<string>(),
  };
}

// Aggregate resolved lines into rollups. Blank-code fallback: a blank AccountCode
// ('') is NOT in the excluded set, so it counts toward account income (and is
// tracked under byCode[UNCODED_ACCOUNT_CODE] — never an empty Firestore key).
// `pendingLines` (Phase 1b) add to a group's pendingAllocationCents — held OUT of
// accountIncome. Excluded set injectable.
export function aggregateRevenueRollups(
  lines: ResolvedRevenueLine[],
  pendingLines: PendingRevenueLine[] = [],
  excluded: readonly string[] = REVENUE_EXCLUDED_ACCOUNT_CODES,
): RevenueAggregation {
  const buckets = new Map<string, Bucket>();
  const bucketFor = (accountId: string, month: string, currency: string): Bucket => {
    const key = bucketKey(accountId, month, currency);
    let b = buckets.get(key);
    if (!b) {
      b = emptyBucket(accountId, month, currency);
      buckets.set(key, b);
    }
    return b;
  };
  let unattributedLineCount = 0;
  let unattributedCents = 0;

  for (const line of lines) {
    if (line.accountId == null) {
      unattributedLineCount += 1;
      unattributedCents += line.amountCents;
      continue;
    }
    const b = bucketFor(line.accountId, line.month, line.currency);
    const code = line.accountCode.trim();
    // Classification runs on the RAW code (blank ∉ excluded → income); only the
    // Firestore map key is sanitised so byCode never carries an empty-string key.
    const codeKey = code === '' ? UNCODED_ACCOUNT_CODE : code;
    b.byCode[codeKey] = (b.byCode[codeKey] ?? 0) + line.amountCents;
    if (isExcludedAccountCode(code, excluded)) {
      b.mediaSpendRecoupedCents += line.amountCents;
    } else {
      b.accountIncomeCents += line.amountCents;
    }
    b._invoiceIds.add(line.invoiceId);
  }

  for (const p of pendingLines) {
    bucketFor(p.accountId, p.month, p.currency).pendingAllocationCents += p.amountCents;
  }

  const rollups: RevenueRollup[] = [...buckets.values()].map((b) => ({
    accountId: b.accountId,
    month: b.month,
    currency: b.currency,
    accountIncomeCents: b.accountIncomeCents,
    mediaSpendRecoupedCents: b.mediaSpendRecoupedCents,
    pendingAllocationCents: b.pendingAllocationCents,
    byCode: b.byCode,
    invoiceCount: b._invoiceIds.size,
  }));

  return { rollups, unattributedLineCount, unattributedCents };
}
